import { ContentButton, Done, LoadStatus, usePortalSettings } from '@servicetitan/cp-ui';
import { Banner, BodyText, Button, Form } from '@servicetitan/design-system';
import { provide, useDependencies } from '@servicetitan/react-ioc';
import classNames from 'classnames';
import { observer } from 'mobx-react';
import { AuthRecoveryRequestPasswordStore } from '../../stores/recovery-request-password.store';
import { ContactInfoMode } from '../../utils/contact-info.validator';
import { enableAutoValidationOnValidationError } from '../../utils/field-auto-validate-helper';
import { handleEmailSpaceKeyDown } from '../../utils/field-email-helper';
import { useAuthHistory, useAuthRecoveryQueryParams } from '../../utils/use-auth-history';
import { AuthLayout } from '../layout/auth.layout';
import { AuthHeadline } from '../layout/components';
import * as Styles from './recovery.module.less';

interface RecoveryModeLabels {
    fieldLabel: string;
    inputType: 'email' | 'tel' | 'text';
    autoComplete: string;
    successDescription: string;
    /** Whether to block the space key (email-only: yes; phone/either: no). */
    blockSpace: boolean;
}

const recoveryLabelsByMode: Record<ContactInfoMode, RecoveryModeLabels> = {
    'email-only': {
        fieldLabel: 'Email',
        inputType: 'email',
        autoComplete: 'email',
        successDescription:
            'If the email is a match in our system, you will receive a password link with instructions on resetting your password to the email on file.',
        blockSpace: true,
    },
    'phone-only': {
        fieldLabel: 'Phone',
        inputType: 'tel',
        autoComplete: 'tel',
        successDescription:
            'If the phone number is a match in our system, you will receive a password link with instructions on resetting your password.',
        blockSpace: false,
    },
    'either': {
        fieldLabel: 'Email or phone number',
        inputType: 'text',
        autoComplete: 'username',
        successDescription:
            'If the email or phone number is a match in our system, you will receive a password link with instructions on resetting your password.',
        blockSpace: false,
    },
};

export const AuthRecoveryRequestPasswordProvider = provide({
    singletons: [AuthRecoveryRequestPasswordStore],
})(({ children }) => children);

export const AuthRecoveryRequestPasswordComponent = observer(() => {
    const [{ canSubmit, contactInfoMode, form, status, submit }] = useDependencies(
        AuthRecoveryRequestPasswordStore
    );
    const { contact } = form.$;
    const labels = recoveryLabelsByMode[contactInfoMode];

    const settings = usePortalSettings();
    const history = useAuthHistory();
    const params = useAuthRecoveryQueryParams();

    return status === LoadStatus.Ok ? (
        <AuthLayout>
            <Done title="Password Link Sent" description={labels.successDescription}>
                <BodyText className="ta-center border-top p-t-2 m-b-4" italic subdued>
                    If you didn't receive a password reset link please contact us at{' '}
                    <span className={classNames('border-bottom', Styles.noWrap)}>
                        {settings?.phoneNumberFormatted}
                    </span>
                    .
                </BodyText>

                <ContentButton width="full" href={history.href('login')}>
                    Back to Sign In
                </ContentButton>
            </Done>
        </AuthLayout>
    ) : (
        <AuthLayout>
            {params.info === 'expired' && (
                <Banner status="warning" icon title="Link expired" className="m-b-2">
                    This password reset link has expired. Please request a new link.
                </Banner>
            )}

            <AuthHeadline
                title="Forgot Password"
                description="Enter your contact information and we'll send you a password reset link."
            />

            <Form>
                <Form.Input
                    label={labels.fieldLabel}
                    className="m-t-2"
                    value={contact.value}
                    onChange={contact.onChangeHandler}
                    onKeyDown={labels.blockSpace ? handleEmailSpaceKeyDown(contact) : undefined}
                    onBlur={enableAutoValidationOnValidationError(contact)}
                    error={contact.error}
                    autoComplete={labels.autoComplete}
                    type={labels.inputType}
                />

                <Button
                    width="full"
                    primary
                    onClick={submit}
                    loading={status === LoadStatus.Loading}
                    disabled={!canSubmit}
                >
                    Send Reset Link
                </Button>

                <ContentButton className="m-t-2" width="full" href={history.href('login')}>
                    Back to Sign In
                </ContentButton>
            </Form>
        </AuthLayout>
    );
});

export const AuthRecoveryRequestPassword = () => (
    <AuthRecoveryRequestPasswordProvider>
        <AuthRecoveryRequestPasswordComponent />
    </AuthRecoveryRequestPasswordProvider>
);
